import { graphql, useMutation } from "@opensea/graphql";
import { Button } from "@opensea/next-ui-kit";
import { Tooltip, useToasts } from "@opensea/ui-kit";
import { Visibility, VisibilityOff } from "@opensea/ui-kit/icons";
import { useBalances } from "@opensea/wallet";
import { useTranslations } from "next-intl";
import { useShallow } from "zustand/react/shallow";
import { useStatusQueryParam } from "@/hooks/query-param/use-status-query-param";
import { useAuthenticatedCallback } from "@/hooks/use-authenticated-callback";
import { useProfileTokensSelectionStore } from "../ProfileTokensSelectionProvider";

// Based on the backend bulk mutations: hideCurrencies and unhideCurrencies
const hideCurrenciesMutation = graphql(`
  mutation HideCurrencies($contractIdentifiers: [ContractIdentifierInput!]!) {
    hideCurrencies(contractIdentifiers: $contractIdentifiers)
  }
`);
const unhideCurrenciesMutation = graphql(`
  mutation UnhideCurrencies($contractIdentifiers: [ContractIdentifierInput!]!) {
    unhideCurrencies(contractIdentifiers: $contractIdentifiers)
  }
`);
export function TokenHideButton({
  onRefetch
}) {
  const t = useTranslations("TokenHideButton");
  const [selectedTokensMap, resetSelections, markTokensMutated] = useProfileTokensSelectionStore(useShallow(state => [state.selectedTokensMap, state.resetSelections, state.markTokensMutated]));
  const {
    statuses
  } = useStatusQueryParam();
  const {
    showSuccessMessage,
    showErrorMessage
  } = useToasts();
  const {
    refreshBalances
  } = useBalances();
  const selectedTokensList = Array.from(selectedTokensMap.values());
  const hasSelectedTokens = selectedTokensList.length > 0;
  const isHiddenStatus = statuses.includes("hidden");
  const [, hideCurrenciesExecutor] = useMutation(hideCurrenciesMutation);
  const [, unhideCurrenciesExecutor] = useMutation(unhideCurrenciesMutation);
  const setCurrencyHidden = useAuthenticatedCallback(async shouldHide => {
    const currencies = selectedTokensList.filter(token => token.chain) // Filter out tokens without chain
    .map(token_0 => ({
      address: token_0.contractAddress,
      chain: token_0.chain // Now guaranteed to be defined
    }));

    // Reset selections immediately for better UX
    resetSelections();
    try {
      // Execute bulk mutation based on desired action:
      // - shouldHide = true (viewing "all") → call hideCurrencies to hide tokens
      // - shouldHide = false (viewing "hidden") → call unhideCurrencies to unhide tokens
      const result = await (shouldHide ? hideCurrenciesExecutor({
        contractIdentifiers: currencies
      }) : unhideCurrenciesExecutor({
        contractIdentifiers: currencies
      }));

      // Check for GraphQL errors
      if (result.error) {
        showErrorMessage(t(shouldHide ? "hideUnknownError" : "unhideUnknownError"));
        if (onRefetch) {
          onRefetch();
        }
        markTokensMutated();
        refreshBalances();
        return;
      }

      // Verify mutation data exists
      if (result.data === undefined) {
        showErrorMessage(t(shouldHide ? "hideUnknownError" : "unhideUnknownError"));
        return;
      }
      showSuccessMessage(t(shouldHide ? "hiddenToast" : "unhiddenToast", {
        count: currencies.length
      }));

      // Refresh data from backend after mutation succeeds
      if (onRefetch) {
        onRefetch();
      }
      markTokensMutated();

      // Refresh balances in the background to keep nav in sync
      refreshBalances();

      // TODO: Use correct analytics event when available
      // await track("Hide Tokens", {
      //   tokenCount: currencies.length,
      //   tokens: currencies.map(currency => ({
      //     id: selectedTokensMap.get(currency.address)?.id,
      //     contractAddress: currency.address,
      //     chain: currency.chain,
      //   })),
      // })
    } catch (error) {
      // Network or unexpected errors
      const errorMessage = error instanceof Error ? error.message : "An unexpected error occurred";
      showErrorMessage(t(shouldHide ? "hideNetworkError" : "unhideNetworkError", {
        message: errorMessage
      }));
    }
  });
  if (!hasSelectedTokens) {
    const actionText = isHiddenStatus ? t("unhide") : t("hide");
    const icon = isHiddenStatus ? Visibility : VisibilityOff;
    return <Button aria-label={t("noSelection")} disabled icon={icon} size="sm" variant="secondary">
        {actionText}
      </Button>;
  }

  // Determine action based on current status
  const shouldHide_0 = !isHiddenStatus; // If we're on "All" status, we should hide tokens
  const actionText_0 = shouldHide_0 ? t("hideCount", {
    count: selectedTokensList.length
  }) : t("unhideCount", {
    count: selectedTokensList.length
  });
  const icon_0 = shouldHide_0 ? VisibilityOff : Visibility;
  const tooltipText = shouldHide_0 ? t("hide") : t("unhide");
  return <Tooltip content={tooltipText}>
      <Button aria-label={`${actionText_0} selected tokens`} icon={icon_0} onClick={() => setCurrencyHidden(shouldHide_0)} size="sm" variant="secondary">
        {actionText_0}
      </Button>
    </Tooltip>;
}